import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, FileText, ImageIcon, DollarSign, Activity, TrendingUp, Share2 } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [trialStats, setTrialStats] = useState(null);
  const [trialWindow, setTrialWindow] = useState(7);

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/users")]).then(([s, u]) => {
      setStats(s.data);
      setUsers(u.data);
    });
  }, []);

  useEffect(() => {
    api.get(`/trial/attribution-stats?days=${trialWindow}`)
      .then((r) => setTrialStats(r.data))
      .catch(() => {});
  }, [trialWindow]);

  if (!stats) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-72 bg-white/5 rounded animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 bg-white/5 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Admin Console</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Platform control</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          Manage tenants, monitor AI usage, and inspect platform health.
        </p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <AdminStat icon={Users} label="Total users" value={stats.total_users} testid="admin-stat-users" />
        <AdminStat icon={FileText} label="Content generated" value={stats.total_content} />
        <AdminStat icon={ImageIcon} label="Logos generated" value={stats.total_logos} />
        <AdminStat icon={DollarSign} label="MRR (sim)" value={`$${stats.mrr.toLocaleString()}`} />
      </div>

      {/* ============ Instant Trial → Signup Funnel ============ */}
      {trialStats && (
        <div
          className="glass-strong rounded-2xl p-6 border border-fuchsia-500/20 bg-gradient-to-br from-fuchsia-500/[0.04] to-cyan-500/[0.04]"
          data-testid="admin-trial-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-400/80 font-bold inline-flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5" /> Instant Trial → Signup Funnel
              </div>
              <h3 className="font-heading text-xl font-bold mt-1">Conversion analytics</h3>
            </div>
            <div className="flex items-center gap-1 rounded-lg bg-black/30 p-1">
              {[7, 30, 0].map((d) => (
                <button
                  key={d}
                  onClick={() => setTrialWindow(d)}
                  data-testid={`trial-window-${d || "all"}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                    trialWindow === d ? "bg-fuchsia-500 text-white" : "text-white/60 hover:text-white"
                  }`}
                >
                  {d === 0 ? "All-time" : `Last ${d}d`}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <TrialStatTile label="Trials generated" value={trialStats.total_trials} accent="violet" />
            <TrialStatTile label="Signups attributed" value={trialStats.attributed_signups} accent="cyan" />
            <TrialStatTile
              label="Conversion rate"
              value={`${trialStats.conversion_rate_pct}%`}
              accent="emerald"
              big
            />
            <TrialStatTile label="Logos rendered" value={trialStats.logos_generated} accent="orange" />
            <TrialStatTile label="Shares unlocked" value={trialStats.shares_captured ?? 0} accent="rose" />
          </div>

          {trialStats.best_utm && (
            <div className="mt-5 flex items-center gap-2 text-sm text-white/70">
              <Share2 className="h-4 w-4 text-cyan-400" />
              <span>
                Best converting UTM:{" "}
                <code className="text-cyan-300 font-mono">{trialStats.best_utm.key}</code>{" "}
                <span className="text-white/40">
                  ({trialStats.best_utm.signups} signup{trialStats.best_utm.signups !== 1 ? "s" : ""})
                </span>
              </span>
            </div>
          )}
        </div>
      )}

      <div className="glass rounded-2xl p-6">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-white/40">Customers</div>
            <h3 className="font-heading text-xl font-bold mt-1">All registered users</h3>
          </div>
          <span className="text-xs font-mono text-white/40">{users.length} total</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.2em] text-white/40 border-b border-white/5">
                <th className="pb-3 pr-4">Name</th>
                <th className="pb-3 pr-4">Email</th>
                <th className="pb-3 pr-4">Role</th>
                <th className="pb-3 pr-4">Onboarded</th>
                <th className="pb-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-user-${u.id}`}>
                  <td className="py-3 pr-4 font-medium">{u.name}</td>
                  <td className="py-3 pr-4 text-white/60 font-mono text-xs">{u.email}</td>
                  <td className="py-3 pr-4">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.role === "admin" ? "brand-gradient" : "glass"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    {u.onboarded ? (
                      <span className="text-green-400 text-xs">✓ Yes</span>
                    ) : (
                      <span className="text-white/40 text-xs">No</span>
                    )}
                  </td>
                  <td className="py-3 text-xs font-mono text-white/50">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-orange-400" />
            <div className="text-xs uppercase tracking-[0.25em] text-white/40">AI usage</div>
          </div>
          <div className="font-heading text-4xl font-black">{stats.ai_credits_used.toLocaleString()}</div>
          <div className="text-xs text-white/40 mt-1 font-mono uppercase tracking-[0.2em]">credits consumed</div>
        </div>
        <div className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">System health</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>API</span><span className="text-green-400">● Operational</span></div>
            <div className="flex justify-between"><span>AI engine</span><span className="text-green-400">● Operational</span></div>
            <div className="flex justify-between"><span>MongoDB</span><span className="text-green-400">● Operational</span></div>
            <div className="flex justify-between"><span>Object storage</span><span className="text-amber-400">● Not configured</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminStat({ icon: Icon, label, value, testid }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.25em] text-white/40">{label}</div>
        <Icon className="h-4 w-4 text-orange-400" />
      </div>
      <div className="font-heading text-3xl font-black mt-3">{value}</div>
    </div>
  );
}

const ACCENT_COLORS = {
  violet:  "text-violet-300",
  cyan:    "text-cyan-300",
  emerald: "text-emerald-300",
  orange:  "text-orange-300",
  rose:    "text-rose-300",
};

function TrialStatTile({ label, value, accent = "violet", big = false }) {
  return (
    <div className="bg-black/30 rounded-xl p-4 border border-white/5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">{label}</div>
      <div className={`font-heading font-black mt-2 ${big ? "text-3xl lg:text-4xl" : "text-2xl"} ${ACCENT_COLORS[accent] || "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
